import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getLang } from "../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const STATUS_LABELS = {
  it: {
    idle: "Tocca il microfono e parla, oppure scrivi",
    listening: "Ti sto ascoltando...",
    thinking: "Sto pensando...",
    speaking: "Sto rispondendo...",
  },
  en: {
    idle: "Tap the mic and speak, or type below",
    listening: "I'm listening...",
    thinking: "Thinking...",
    speaking: "Answering...",
  },
};

export function useAssistant() {
  const [state, setState] = useState("idle");
  const [messages, setMessages] = useState([]);
  const [muted, setMuted] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(true);
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const mutedRef = useRef(false);

  useEffect(() => {
    if (!(window.SpeechRecognition || window.webkitSpeechRecognition)) {
      setSpeechSupported(false);
    }
  }, []);

  const stopAudio = () => {
    audioRef.current?.pause();
    window.speechSynthesis?.cancel();
  };

  const browserSpeak = (text) => {
    if (!window.speechSynthesis) {
      setState("idle");
      return;
    }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = getLang() === "en" ? "en-US" : "it-IT";
    u.rate = 1.02;
    u.onend = () => setState("idle");
    u.onerror = () => setState("idle");
    setState("speaking");
    window.speechSynthesis.speak(u);
  };

  const speak = async (text) => {
    if (mutedRef.current) {
      setState("idle");
      return;
    }
    setState("speaking");
    try {
      const resp = await fetch(`${API}/assistant/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!resp.ok) throw new Error("tts failed");
      const { url } = await resp.json();
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setState("idle");
      audio.onerror = () => setState("idle");
      await audio.play();
    } catch {
      browserSpeak(text);
    }
  };

  const sendMessage = async (raw) => {
    const text = (raw || "").trim();
    if (!text) return;
    stopAudio();
    setState("thinking");
    const history = messages.slice(-8);
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);

    let full = "";
    try {
      const resp = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: text, history }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data:")) continue;
          const payload = JSON.parse(part.slice(5).trim());
          if (payload.error) throw new Error(payload.error);
          if (payload.delta) {
            full += payload.delta;
            const snapshot = full;
            setMessages((m) => {
              const next = [...m];
              next[next.length - 1] = { role: "assistant", text: snapshot };
              return next;
            });
          }
        }
      }
    } catch (e) {
      const en = getLang() === "en";
      const msg = e.message && e.message.includes("Troppe richieste")
        ? (en ? "Too many questions in a row. Try again shortly." : "Hai fatto troppe domande di fila. Riprova tra poco.")
        : (en ? "Sorry, something went wrong. Please try again soon." : "Mi dispiace, si è verificato un errore. Riprova tra poco.");
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = { role: "assistant", text: msg };
        return next;
      });
      setState("idle");
      return;
    }
    speak(full);
  };

  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setSpeechSupported(false);
      toast.error(getLang() === "en"
        ? "Speech recognition is not supported by this browser. Please type instead."
        : "Il riconoscimento vocale non è supportato da questo browser. Usa la scrittura.");
      return;
    }
    if (state === "listening") {
      recognitionRef.current?.stop();
      setState("idle");
      return;
    }
    stopAudio();
    const rec = new SR();
    rec.lang = getLang() === "en" ? "en-US" : "it-IT";
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setState("idle");
      sendMessage(transcript);
    };
    rec.onerror = () => setState("idle");
    rec.onend = () => setState((s) => (s === "listening" ? "idle" : s));
    recognitionRef.current = rec;
    rec.start();
    setState("listening");
  };

  const toggleMute = () => {
    const next = !muted;
    mutedRef.current = next;
    setMuted(next);
    if (next) {
      stopAudio();
      setState((s) => (s === "speaking" ? "idle" : s));
    }
  };

  const greet = (text) => {
    setMessages((m) => (m.length ? m : [{ role: "assistant", text, greeting: true }]));
    speak(text);
  };

  return { state, messages, sendMessage, toggleMic, muted, toggleMute, speechSupported, speak, greet };
}
